import { useEffect, useState } from "react";
import { Copy, Handshake, Share2, Trophy } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

export function AffiliateCard() {
  const [data, setData] = useState(null);
  const [board, setBoard] = useState(null);
  const [agreed, setAgreed] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = () => {
    api.get("/affiliate/me").then((r) => {
      setData(r.data);
      if (r.data.active) api.get("/affiliate/leaderboard").then((b) => setBoard(b.data)).catch(() => {});
    }).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const activate = async () => {
    setBusy(true);
    try {
      await api.post("/affiliate/accept-terms");
      toast.success("Affiliate link activated");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not activate");
    } finally { setBusy(false); }
  };

  const copy = () => {
    navigator.clipboard.writeText(data.link);
    toast.success("Affiliate link copied — remember to disclose #Ad when sharing");
  };

  if (!data) return null;

  return (
    <div className="mt-8 rounded-2xl border border-emerald-500/30 bg-emerald-500/5 p-6 md:p-8" data-testid="affiliate-card">
      <div className="flex items-center gap-2">
        <Handshake className="h-5 w-5 text-emerald-400" />
        <h2 className="font-display text-xl">Affiliate earnings — get paid in cash</h2>
      </div>
      <p className="mt-1.5 text-sm text-muted-foreground">
        Earn <strong className="text-emerald-400">20% of the first verified Pro payment</strong> and <strong className="text-emerald-400">10% of the first verified Business payment</strong> from
        anyone who signs up through your link. Commission is only created on a verified payment — never a free signup.
      </p>

      {!data.active ? (
        <div className="mt-5 rounded-xl border border-border bg-card p-4" data-testid="affiliate-terms-gate">
          <p className="text-xs text-muted-foreground">
            Before your link is generated you must agree to the affiliate rules: always disclose
            (e.g. <em>"#Ad — I may earn a commission if you sign up through my Zuntria link"</em>),
            no misleading claims, no guaranteed-income promises, no spam, no self-referrals.
            Commissions have a 30-day cooling-off, a £20 payout threshold and are paid monthly via PayPal.
            Refunds reverse commissions. See the <a href="/terms" className="text-primary hover:underline">Terms</a>.
          </p>
          <label className="mt-3 flex items-start gap-2 text-xs">
            <input type="checkbox" checked={agreed} onChange={(e) => setAgreed(e.target.checked)}
              className="mt-0.5 h-4 w-4 rounded border-border accent-primary" data-testid="affiliate-terms-checkbox" />
            <span>I agree to the affiliate terms and disclosure rules</span>
          </label>
          <Button onClick={activate} disabled={!agreed || busy} className="mt-3 bg-emerald-500 text-white hover:bg-emerald-600"
            data-testid="affiliate-activate-btn">
            {busy ? "Activating…" : "Activate my affiliate link"}
          </Button>
        </div>
      ) : (
        <>
          <div className="mt-5 flex items-center gap-2 rounded-md border border-border bg-background px-3 py-2.5">
            <div className="flex-1 truncate text-sm" data-testid="affiliate-link">{data.link}</div>
            <Button size="sm" variant="ghost" className="h-10 w-10 p-0" onClick={copy} data-testid="affiliate-copy-btn">
              <Copy className="h-3.5 w-3.5" />
            </Button>
            <Button size="sm" variant="ghost" className="h-10 w-10 p-0" data-testid="affiliate-share-btn"
              onClick={() => window.open(`https://wa.me/?text=${encodeURIComponent(`#Ad — I may earn a commission if you sign up through my Zuntria link: ${data.link}`)}`, "_blank")}>
              <Share2 className="h-3.5 w-3.5" />
            </Button>
          </div>
          <div className="mt-4 grid grid-cols-3 gap-3 md:grid-cols-6" data-testid="affiliate-stats">
            <AffStat label="Clicks" value={data.stats.clicks} />
            <AffStat label="Signups" value={data.stats.signups} />
            <AffStat label="Paid referrals" value={data.stats.paid_referrals} />
            <AffStat label="Pending" value={`£${data.stats.pending.toFixed(2)}`} muted />
            <AffStat label="Approved" value={`£${data.stats.approved.toFixed(2)}`} accent />
            <AffStat label="Paid out" value={`£${data.stats.paid.toFixed(2)}`} accent />
          </div>
          <p className="mt-3 text-[11px] text-muted-foreground" data-testid="affiliate-truth-note">
            <strong>Pending</strong> commissions approve after a 30-day cooling-off (refunds reverse them).
            Only <strong>Approved</strong> balance counts toward the monthly PayPal payout (£20 minimum).
            Pending money is never money you have received.
          </p>
          {data.stats.paid + data.stats.approved > 0 && (
            <Button size="sm" variant="outline" className="mt-3" data-testid="affiliate-share-win-btn"
              onClick={() => {
                navigator.clipboard.writeText(`I've earned £${(data.stats.approved + data.stats.paid).toFixed(2)} in verified affiliate commissions with Zuntria. #Ad — I may earn a commission if you sign up through my link: ${data.link}`);
                toast.success("Win card copied — paste it anywhere");
              }}>
              <Share2 className="mr-1.5 h-3.5 w-3.5" /> Share my win
            </Button>
          )}
          {board && board.leaderboard.length > 0 && (
            <div className="mt-5 rounded-xl border border-border bg-background p-4" data-testid="affiliate-leaderboard">
              <div className="flex items-center gap-1.5">
                <Trophy className="h-4 w-4 text-amber-400" />
                <p className="text-xs font-semibold uppercase tracking-wider">Top affiliates — verified earnings</p>
                {board.my_rank && <span className="ml-auto text-[10px] text-emerald-400">You're #{board.my_rank}</span>}
              </div>
              <ul className="mt-2 space-y-1.5">
                {board.leaderboard.slice(0, 5).map((r) => (
                  <li key={r.rank} className={`flex items-center justify-between text-xs ${r.is_me ? "text-emerald-400 font-semibold" : ""}`} data-testid={`leaderboard-row-${r.rank}`}>
                    <span>#{r.rank} {r.name}{r.is_me ? " (you)" : ""}</span>
                    <span>£{r.earned_gbp.toFixed(2)} · {r.conversions} conv.</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function AffStat({ label, value, accent, muted }) {
  return (
    <div className="rounded-lg border border-border bg-background p-3 text-center">
      <p className={`font-display text-lg ${accent ? "text-emerald-400" : muted ? "text-amber-400" : ""}`}>{value}</p>
      <p className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</p>
    </div>
  );
}
